import React from "react";
import { Helmet } from "react-helmet-async";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SiteSettingsContext";

// Site URL base for canonical / hreflang / OG URLs.
// In prod CRA inlines env at build time. Runtime fallback keeps SSR-ish
// parity when the bundle is served from a different host.
const SITE_URL = (
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "https://AR.wheretech.it")
).replace(/\/$/, "");

function toAbsolute(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Emits the full SEO head for a public page.
 * - title, description: already translated strings
 * - pathIt, pathEn: localized URL paths for this page (e.g. "/it", "/en")
 * - image: OG/Twitter image, absolute or site-relative; falls back to og_image_default
 * - type: OpenGraph type ("website" | "article" | ...)
 * - jsonLd: optional JSON-LD object to inline as <script type="application/ld+json">
 */
export default function PageSeo({ title, description, pathIt, pathEn, image, type = "website", jsonLd }) {
  const { lang } = useLang();
  const settings = useSettings();

  const currentPath = lang === "en" ? pathEn : pathIt;
  const canonical = `${SITE_URL}${currentPath}`;
  const altIt = `${SITE_URL}${pathIt}`;
  const altEn = `${SITE_URL}${pathEn}`;

  const ogImage = toAbsolute(image || settings.og_image_default);
  const ogLocale = lang === "en" ? "en_US" : "it_IT";
  const ogLocaleAlt = lang === "en" ? "it_IT" : "en_US";
  const siteName = settings.hotel_name || "Hotel Alpi Resort";

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="it" href={altIt} />
      <link rel="alternate" hrefLang="en" href={altEn} />
      <link rel="alternate" hrefLang="x-default" href={altIt} />

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {settings.twitter_handle && (
        <meta name="twitter:site" content={settings.twitter_handle} />
      )}

      {/* Google Search Console (optional, empty until provisioned) */}
      {settings.google_site_verification && (
        <meta name="google-site-verification" content={settings.google_site_verification} />
      )}

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

/**
 * Convenience: build the JSON-LD Hotel schema from settings.
 * Call from HomePage and pass to <PageSeo jsonLd={hotelJsonLd(settings)} />.
 */
export function hotelJsonLd(settings, { lang = "it" } = {}) {
  const siteName = settings.hotel_name || "Hotel Alpi Resort";
  const description = lang === "en"
    ? settings.home_meta_desc_en
    : settings.home_meta_desc_it;
  const image = toAbsolute(settings.og_image_default);
  return {
    "@context": "https://schema.org",
    "@type": "Hotel",
    name: siteName,
    description,
    url: `${SITE_URL}${lang === "en" ? "/en" : "/it"}`,
    image: image ? [image] : undefined,
    priceRange: settings.price_range || "€€€",
    telephone: settings.phone,
    email: settings.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: settings.address,
      addressLocality: settings.address_locality || "Torino",
      addressRegion: settings.address_region || "TO",
      postalCode: settings.postal_code || "10123",
      addressCountry: settings.country_code || "IT",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: settings.map_lat,
      longitude: settings.map_lng,
    },
    sameAs: Object.values(settings.social || {}).filter(Boolean),
  };
}
